import axios from 'axios';
import { getAuthStorage } from '../utils/pwaUtils';

const API_URL = process.env.REACT_APP_API_URL + '/rbac';

// Get token from storage
const getHeaders = () => {
    const storage = getAuthStorage();
    const token = storage.getItem('token');
    return {
        headers: { Authorization: `Bearer ${token}` }
    };
};

const rbacService = {
    // Permissions
    getPermissions: async (page, limit, search, sortBy, sortOrder) => {
        const query = page ? `?page=${page}&limit=${limit}&search=${search}&sortBy=${sortBy || ''}&sortOrder=${sortOrder || ''}` : '';
        const response = await axios.get(`${API_URL}/permissions${query}`, getHeaders());
        return response.data;
    },
    createPermission: async (slug, description) => {
        const response = await axios.post(`${API_URL}/permissions`, { slug, description }, getHeaders());
        return response.data;
    },
    updatePermission: async (id, data) => {
        const response = await axios.put(`${API_URL}/permissions/${id}`, data, getHeaders());
        return response.data;
    },
    deletePermission: async (id) => {
        const response = await axios.delete(`${API_URL}/permissions/${id}`, getHeaders());
        return response.data;
    },

    // Groups
    getGroups: async (page, limit, search, sortBy, sortOrder) => {
        const query = page ? `?page=${page}&limit=${limit}&search=${search}&sortBy=${sortBy || ''}&sortOrder=${sortOrder || ''}` : '';
        const response = await axios.get(`${API_URL}/groups${query}`, getHeaders());
        return response.data;
    },
    createGroup: async (name, description) => {
        const response = await axios.post(`${API_URL}/groups`, { name, description }, getHeaders());
        return response.data;
    },
    updateGroup: async (id, data) => {
        const response = await axios.put(`${API_URL}/groups/${id}`, data, getHeaders());
        return response.data;
    },
    deleteGroup: async (id) => {
        const response = await axios.delete(`${API_URL}/groups/${id}`, getHeaders());
        return response.data;
    },
    addPermissionToGroup: async (groupId, permissionId) => {
        const response = await axios.post(`${API_URL}/groups/${groupId}/permissions`, { permissionId }, getHeaders());
        return response.data;
    },
    removePermissionFromGroup: async (groupId, permissionId) => {
        const response = await axios.delete(`${API_URL}/groups/${groupId}/permissions/${permissionId}`, getHeaders());
        return response.data;
    },
    syncPermissionsForGroup: async (groupId, permissionIds) => {
        const response = await axios.put(`${API_URL}/groups/${groupId}/permissions/sync`, { permissionIds }, getHeaders());
        return response.data;
    },
    syncRolesForGroup: async (groupId, roles) => {
        const response = await axios.put(`${API_URL}/groups/${groupId}/roles/sync`, { roles }, getHeaders());
        return response.data;
    },

    // Roles (Profile Types)
    getRoles: async (page, limit, search, sortBy, sortOrder) => {
        const query = page ? `?page=${page}&limit=${limit}&search=${search}&sortBy=${sortBy || ''}&sortOrder=${sortOrder || ''}` : '';
        const response = await axios.get(`${API_URL}/roles${query}`, getHeaders());
        return response.data;
    },
    createRole: async (data) => {
        const response = await axios.post(`${API_URL}/roles`, data, getHeaders());
        return response.data;
    },
    updateRole: async (id, data) => {
        const response = await axios.put(`${API_URL}/roles/${id}`, data, getHeaders());
        return response.data;
    },
    deleteRole: async (id) => {
        const responseActual = await axios.delete(`${API_URL}/roles/${id}`, getHeaders());
        return responseActual.data;
    },
    syncGroupsForRole: async (roleId, groups) => {
        const response = await axios.put(`${API_URL}/roles/${roleId}/groups/sync`, { groups }, getHeaders());
        return response.data;
    },
    syncPermissionsForRole: async (roleId, permissionIds) => {
        const response = await axios.put(`${API_URL}/roles/${roleId}/permissions/sync`, { permissionIds }, getHeaders());
        return response.data;
    },

    // User/Profile Management
    searchUsers: async (query) => {
        const response = await axios.get(`${API_URL}/users/search?q=${query}`, getHeaders());
        return response.data;
    },
    setRoleForProfile: async (profileId, roleId) => {
        const response = await axios.post(`${API_URL}/profiles/${profileId}/role`, { roleId }, getHeaders());
        return response.data;
    },
    assignGroupsToProfile: async (profileId, groupIds) => {
        const response = await axios.post(`${API_URL}/profiles/${profileId}/groups/bulk`, { groupIds }, getHeaders());
        return response.data;
    },
    assignPermissionsToProfile: async (profileId, permissionIds) => {
        const response = await axios.post(`${API_URL}/profiles/${profileId}/permissions/bulk`, { permissionIds }, getHeaders());
        return response.data;
    },
    deleteUser: async (id) => {
        const response = await axios.delete(`${API_URL}/users/${id}`, getHeaders());
        return response.data;
    },

    // User/Profile Management (New Lifecycle)
    getUsersPaginated: async (page = 1, limit = 10, search = '', sortBy = '', sortOrder = '') => {
        const response = await axios.get(`${API_URL}/users?page=${page}&limit=${limit}&search=${search}&sortBy=${sortBy}&sortOrder=${sortOrder}`, getHeaders());
        return response.data;
    },
    createProfile: async (userId, roleId, artistName) => {
        const response = await axios.post(`${API_URL}/profiles`, { userId, roleId, artistName }, getHeaders());
        return response.data;
    },
    deleteProfile: async (profileId) => {
        const response = await axios.delete(`${API_URL}/profiles/${profileId}`, getHeaders());
        return response.data;
    },
    getProfilePermissions: async (profileId) => {
        const response = await axios.get(`${API_URL}/profiles/${profileId}/permissions`, getHeaders());
        return response.data;
    },
    verifyProfile: async (profileId, until) => {
        const response = await axios.patch(`${API_URL}/profiles/${profileId}/verify`, { until }, getHeaders());
        return response.data;
    },
    unverifyProfile: async (profileId) => {
        const response = await axios.delete(`${API_URL}/profiles/${profileId}/verify`, getHeaders());
        return response.data;
    },

    // Route Configs
    getRouteConfigs: async (search = '') => {
        const query = search ? `?search=${search}` : '';
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/rbac/routes${query}`, getHeaders());
        return response.data;
    },
    createRouteConfig: async (data) => {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/rbac/routes`, data, getHeaders());
        return response.data;
    },
    updateRouteConfig: async (id, data) => {
        const response = await axios.put(`${process.env.REACT_APP_API_URL}/rbac/routes/${id}`, data, getHeaders());
        return response.data;
    },
    deleteRouteConfig: async (id) => {
        const response = await axios.delete(`${process.env.REACT_APP_API_URL}/rbac/routes/${id}`, getHeaders());
        return response.data;
    }
};

export default rbacService;
